import { useState, useMemo } from 'react';
import { Input } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '~/components/atoms/LoadingSpinner';
import { ErrorMessage } from '~/components/atoms/ErrorMessage';
import { useCategoryByIdService } from '~/services/categoriesService/categories.service';
import { FoodCard } from '~/components/molecules/FoodCard';
import { ROUTES } from '~/constants/routes.contants';
import { useLocale } from '~/hooks/useLocale';
import { useFoodsByCategoryService } from '~/hooks/useFood';
import { Breadcrumbs } from '~/components/molecules/Breadcrumbs/Breadcrumbs';
import { FoodResponseDto } from '@bitecritic-api/src/foods/dto/food.response.dto';
import { getIdFromSlug } from '~/utils/slug-utils';
import { SEO } from '~/components/molecules/SEO/SEO';

interface GroupedFoods {
  [key: string]: Array<FoodResponseDto>;
}

export default function CategoryDetail() {
  const { t } = useTranslation();
  const { nextRouter } = useLocale();
  const [search, setSearch] = useState('');
  const { id } = nextRouter.query;
  const categoryId = useMemo(() => {
    if (!nextRouter.isReady || typeof id !== 'string') return undefined;
    const parsedId = getIdFromSlug(id);
    return parsedId ? Number.parseInt(parsedId) : undefined;
  }, [id, nextRouter.isReady]);

  const { data: category, isLoading: isCategoryLoading } = useCategoryByIdService(categoryId);
  const { data: foods, isLoading: isFoodsLoading, error } = useFoodsByCategoryService(categoryId);

  const groupedFoods = useMemo(() => {
    if (!foods) return {};
    if (!search.trim()) {
      return foods.reduce((acc: GroupedFoods, food) => {
        const firstLetter = food.name.charAt(0).toUpperCase();
        if (!acc[firstLetter]) {
          acc[firstLetter] = [];
        }
        acc[firstLetter]!.push(food);
        return acc;
      }, {});
    }

    const searchLower = search.toLowerCase();
    const filteredFoods = foods.filter(food =>
      food.name.toLowerCase().includes(searchLower)
    );

    return filteredFoods.reduce((acc: GroupedFoods, food) => {
      const firstLetter = food.name.charAt(0).toUpperCase();
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter]!.push(food);
      return acc;
    }, {});
  }, [foods, search]);

  if (isCategoryLoading || isFoodsLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={t('common.error.default')} />;

  // Generate SEO title and description
  const seoTitle = category ? `${category.name} - ${t('categories.meta.categoryTitle')}` : t('categories.meta.loading');
  const seoDescription = category
    ? t('categories.meta.categoryDescription', {
      category: category.name,
      count: foods?.length || 0
    })
    : t('categories.meta.defaultDescription');

  // Generate keywords
  const keywords = [
    category?.name,
    t('categories.meta.keywords'),
    ...(foods?.slice(0, 5).map(food => food.name) || [])
  ].filter(Boolean).join(', ');

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        type="website"
        keywords={keywords}
        image={category?.mainImageId || '/images/bg-hero.jpg'}
      />
      <div className="space-y-4">
        <Breadcrumbs items={[
          { label: t('categories.title'), href: ROUTES.CATEGORIES },
          { label: category?.name || '', href: undefined }
        ]} />
        <div className="flex items-center justify-between">
          <div className="flex-1 max-w-sm p-4">
            <Input
              value={search}
              onValueChange={setSearch}
              placeholder={t('common.search') || ''}
              isClearable
            />
          </div>
        </div>
        <div className="min-h-screen pb-8">
          <div className="px-4">
            {Object.entries(groupedFoods)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([letter, letterFoods]) => (
                <div key={letter} className="mb-12">
                  <h2 className="text-xl font-semibold mb-6 text-primary">{letter}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {letterFoods
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((food) => (
                        <FoodCard
                          key={food.id}
                          data={food}
                        />
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
} 